import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, FileText, BarChart3, Calendar, Users, 
  LogOut, Sun, Moon, Menu, X, TrendingUp, DollarSign,
  PiggyBank, UserCheck, UserPlus, CreditCard, Store, Home, User, Package,
  Building2, UserCircle, Layers, Pencil, UserCog, KeyRound, Trophy
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { cn } from "./lib/utils";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LancamentosPage from "./pages/LancamentosPage";
import RelatoriosPage from "./pages/RelatoriosPage";
import DetalhamentoPage from "./pages/DetalhamentoPage";
import CRMPage from "./pages/CRMPage";
import CadastrarPage from "./pages/CadastrarPage";
import FiadoPage from "./pages/FiadoPage";
import FiadoPublicoPage from "./pages/FiadoPublicoPage";
import RevendaPage from "./pages/RevendaPage";
import RevendaPublicoPage from "./pages/RevendaPublicoPage";
import AcomodadoPage from "./pages/AcomodadoPage";
import PerfilPage from "./pages/PerfilPage";
import PerfilPublicoPage from "./pages/PerfilPublicoPage";
import ProdutosPage from "./pages/ProdutosPage";
import RevendaAcomodadoPage from "./pages/RevendaAcomodadoPage";
import RevAcomodadoPublicoPage from "./pages/RevAcomodadoPublicoPage";
import HubEmpresaFuncPage from "./pages/HubEmpresaFuncPage";
import FuncionariosPage from "./pages/FuncionariosPage";
import FuncionarioPublicoPage from "./pages/FuncionarioPublicoPage";
import CreditosPage from "./pages/CreditosPage";
import HubGeralPage from "./pages/HubGeralPage";
import HubEmpresaPage from "./pages/HubEmpresaPage";
import HubPessoalPage from "./pages/HubPessoalPage";
import BolaoPage from "./pages/BolaoPage";
import BolaoPublicoPage from "./pages/BolaoPublicoPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (err) {
        localStorage.removeItem("token");
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const authAxios = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, authAxios }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "dark";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Navigation Items
const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/lancamentos", label: "Lancamentos", icon: FileText },
  { path: "/hub-geral", label: "Hub Geral", icon: Layers },
  { path: "/hub-empresa", label: "Hub Empresa", icon: Building2 },
  { path: "/hub-pessoal", label: "Hub Pessoal", icon: UserCircle },
  { path: "/funcionarios", label: "Funcionarios", icon: UserCog },
  { path: "/crm", label: "CRM", icon: Users },
  { path: "/fiado", label: "Fiado", icon: CreditCard },
  { path: "/revenda", label: "Revenda", icon: Store },
  { path: "/produtos", label: "Produtos", icon: Package },
  { path: "/creditos", label: "Creditos", icon: KeyRound },
  { path: "/acomodado", label: "Acomodado", icon: Home },
  { path: "/revenda-acomodado", label: "Rev. Acomodado", icon: Store },
  { path: "/bolao", label: "Bolão", icon: Trophy },
  { path: "/perfil", label: "Perfil", icon: User },
  { path: "/cadastrar", label: "Cadastrar", icon: UserPlus, adminOnly: true },
];

// Sidebar Component
const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, authAxios } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState("https://customer-assets.emergentagent.com/job_finboard-15/artifacts/gxw2xuac_nexnovo.png");
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [newLogoUrl, setNewLogoUrl] = useState("");

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await authAxios.get("/perfil");
        if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
      } catch {}
    };
    fetchLogo();
  }, [authAxios]);

  const handleSaveLogo = async () => {
    if (!newLogoUrl.trim()) return;
    try {
      await authAxios.put("/perfil", { logo_url: newLogoUrl.trim() });
      setLogoUrl(newLogoUrl.trim());
      setLogoDialogOpen(false);
      toast.success("Logo atualizada!");
    } catch { toast.error("Erro ao salvar logo"); }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Logout realizado com sucesso");
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transition-transform duration-300",
          "lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex flex-col items-center justify-center p-6 border-b border-border relative group">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-20 h-20 object-contain cursor-pointer"
              onClick={() => { setNewLogoUrl(logoUrl); setLogoDialogOpen(true); }}
              title="Clique para trocar a logo"
            />
            <button 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md bg-muted/80 hover:bg-muted text-muted-foreground"
              onClick={() => { setNewLogoUrl(logoUrl); setLogoDialogOpen(true); }}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems
              .filter(item => !item.adminOnly || user?.is_admin)
              .map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start gap-3 h-11",
                    isActive && "bg-primary/10 text-primary hover:bg-primary/20"
                  )}
                  onClick={() => { navigate(item.path); onClose(); }}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border space-y-4">
            {/* User Info */}
            <div className="flex items-center gap-3 px-2">
              <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>

            {/* Theme Toggle */}
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? (
                <>
                  <Sun className="w-5 h-5" />
                  Modo Claro
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  Modo Escuro
                </>
              )}
            </Button>

            {/* Logout */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Trocar Logo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>URL da imagem</Label>
              <Input value={newLogoUrl} onChange={(e) => setNewLogoUrl(e.target.value)} placeholder="https://..." data-testid="input-logo-url" />
            </div>
            {newLogoUrl && (
              <div className="flex justify-center p-4 rounded-lg bg-muted/50 border border-border/50">
                <img src={newLogoUrl} alt="Preview" className="max-w-[120px] max-h-[120px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setLogoDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveLogo} data-testid="save-logo-btn">Salvar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Layout
const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const currentPage = navItems.find(item => item.path === location.pathname)?.label || "Dashboard";

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 glass-panel border-b border-border/40 px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-semibold">{currentPage}</h1>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

// App Component
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/revenda-publico/:token" element={<RevendaPublicoPage />} />
            <Route path="/rev-acomodado-publico/:token" element={<RevAcomodadoPublicoPage />} />
            <Route path="/hub-empresa-func/:token" element={<HubEmpresaFuncPage />} />
            <Route path="/funcionario/:token" element={<FuncionarioPublicoPage />} />
            <Route path="/fiado-publico/:token" element={<FiadoPublicoPage />} />
            <Route path="/perfil-publico/:token" element={<PerfilPublicoPage />} />
            <Route path="/bolao/:codigo" element={<BolaoPublicoPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/lancamentos" element={<ProtectedRoute><LancamentosPage /></ProtectedRoute>} />
            <Route path="/relatorios" element={<Navigate to="/lancamentos" replace />} />
            <Route path="/detalhamento" element={<Navigate to="/lancamentos" replace />} />
            <Route path="/hub-geral" element={<ProtectedRoute><HubGeralPage /></ProtectedRoute>} />
            <Route path="/hub-empresa" element={<ProtectedRoute><HubEmpresaPage /></ProtectedRoute>} />
            <Route path="/hub-pessoal" element={<ProtectedRoute><HubPessoalPage /></ProtectedRoute>} />
            <Route path="/funcionarios" element={<ProtectedRoute><FuncionariosPage /></ProtectedRoute>} />
            <Route path="/crm" element={<ProtectedRoute><CRMPage /></ProtectedRoute>} />
            <Route path="/fiado" element={<ProtectedRoute><FiadoPage /></ProtectedRoute>} />
            <Route path="/revenda" element={<ProtectedRoute><RevendaPage /></ProtectedRoute>} />
            <Route path="/produtos" element={<ProtectedRoute><ProdutosPage /></ProtectedRoute>} />
            <Route path="/creditos" element={<ProtectedRoute><CreditosPage /></ProtectedRoute>} />
            <Route path="/acomodado" element={<ProtectedRoute><AcomodadoPage /></ProtectedRoute>} />
            <Route path="/revenda-acomodado" element={<ProtectedRoute><RevendaAcomodadoPage /></ProtectedRoute>} />
            <Route path="/bolao" element={<ProtectedRoute><BolaoPage /></ProtectedRoute>} />
            <Route path="/perfil" element={<ProtectedRoute><PerfilPage /></ProtectedRoute>} />
            <Route path="/cadastrar" element={<ProtectedRoute adminOnly><CadastrarPage /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
